/**
 * Why a member is opening a membership checkout. Derived at open time from the
 * member's state (or a `?intent=` deep link); never stored on the Booker.
 * When absent, every consumer keeps its default copy and behaviour.
 */
export type MembershipIntentKind = "RENEW" | "JOIN" | "CHANGE_TIER";

export type MembershipCheckoutIntent = {
  kind: MembershipIntentKind;
  organizationId: string;
  /** Tag id of the member's current tier, for RENEW / CHANGE_TIER. */
  currentTierTagId?: string | null;
  currentTierName?: string | null;
  /** Tier picked in the Memberships tier picker; preselected over the current one. */
  targetTierTagId?: string | null;
  /** Businesses to preselect in a multi-business checkout (the ones renewing). */
  coveredOperatorIds?: string[];
  membershipNomenclature: string;
  memberNomenclature: string;
};

export const MEMBERSHIP_INTENT_PARAM = "intent";
/** `UserMembershipRow._id` the intent applies to. */
export const MEMBERSHIP_ROW_PARAM = "membership";
export const MEMBERSHIP_DEEP_LINK_PARAMS = [
  MEMBERSHIP_INTENT_PARAM,
  MEMBERSHIP_ROW_PARAM,
] as const;

const PARAM_TO_KIND: Record<string, MembershipIntentKind> = {
  renew: "RENEW",
  join: "JOIN",
  change_tier: "CHANGE_TIER",
};

export const parseMembershipIntentParam = (
  value: string | null | undefined,
): MembershipIntentKind | null =>
  (value && PARAM_TO_KIND[value.toLowerCase()]) || null;

export const membershipIntentToParam = (kind: MembershipIntentKind): string =>
  kind.toLowerCase();

/** `/app` link that opens a membership broadcast with checkout intent. */
export function buildMembershipBroadcastPath({
  broadcastId,
  intent,
  membershipId,
}: {
  broadcastId: string;
  intent: MembershipIntentKind;
  membershipId?: string | null;
}): string {
  const params = new URLSearchParams({ broadcast: broadcastId });
  params.set(MEMBERSHIP_INTENT_PARAM, membershipIntentToParam(intent));
  if (membershipId) params.set(MEMBERSHIP_ROW_PARAM, membershipId);
  return `/app?${params.toString()}`;
}

/**
 * `intent` / `membership` from a deep link, for forwarding alongside
 * `broadcast` through login and onboarding redirects. Unknown intents drop.
 */
export function readMembershipDeepLinkParams(
  params: URLSearchParams = new URLSearchParams(window.location.search),
): { intent: string | null; membership: string | null } {
  const intent = parseMembershipIntentParam(params.get(MEMBERSHIP_INTENT_PARAM));
  return {
    intent: intent ? membershipIntentToParam(intent) : null,
    membership: intent ? params.get(MEMBERSHIP_ROW_PARAM) : null,
  };
}

export type MembershipRowLike = {
  _id: string;
  organizationId: string;
  needsRenewal?: boolean | null;
  tierTagId?: string | null;
  tierName?: string | null;
  renewablePurchaseId?: string | null;
  renewalBilling?: string | null;
  membershipNomenclature?: string | null;
  memberNomenclature?: string | null;
};

/**
 * Picks the membership row a deep link refers to: the explicit row when given,
 * otherwise the org's first row due for renewal, otherwise its first row.
 */
export function resolveMembershipRowForIntent<T extends MembershipRowLike>(
  memberships: readonly T[],
  { membershipId, organizationId }: {
    membershipId?: string | null;
    organizationId?: string | null;
  },
): T | null {
  if (membershipId) {
    return memberships.find(m => m._id === membershipId) ?? null;
  }
  if (!organizationId) return null;
  const inOrg = memberships.filter(m => m.organizationId === organizationId);
  return inOrg.find(m => m.needsRenewal) ?? inOrg[0] ?? null;
}

/**
 * How a member renews. Purchase-backed memberships renew through the
 * storefront renewal engine (auto-charge or a renewal invoice); opening a new
 * checkout for them would create a second purchase. Only memberships with no
 * covering purchase renew by checkout.
 */
export type MembershipRenewalAction = "AUTO_CHARGE" | "INVOICE" | "CHECKOUT";

export function getMembershipRenewalAction(
  row: Pick<MembershipRowLike, "renewablePurchaseId" | "renewalBilling">,
): MembershipRenewalAction {
  if (!row.renewablePurchaseId) return "CHECKOUT";
  return row.renewalBilling === "AUTO_CHARGE" ? "AUTO_CHARGE" : "INVOICE";
}

type TierCheckoutLike = {
  tierTagId?: string | null;
  bookerId?: string | null;
  free?: boolean | null;
};

type ResourceLike = {
  _id?: string | null;
  type: string;
  booker?: { _id?: string | null } | null;
} | null;

export type MembershipCheckoutTarget = {
  /** Storefront resource the checkout opens from. */
  resourceId: string;
  /** The tier's own checkout when the broadcast doesn't include it; replaces the resource's. */
  bookerId?: string;
};

/**
 * Where a membership checkout opens for `tierTagId`. The org's tier → checkout
 * mapping wins: its storefront resource when the broadcast has exactly one,
 * otherwise that checkout opened from the broadcast's first storefront. An
 * unmapped tier falls back to the only storefront resource. Returns null when
 * ambiguous, so callers open the broadcast as a collection instead of guessing.
 */
export function resolveMembershipCheckoutTarget({
  resources,
  tierCheckouts,
  tierTagId,
}: {
  resources: readonly ResourceLike[];
  tierCheckouts?: readonly (TierCheckoutLike | null)[] | null;
  tierTagId?: string | null;
}): MembershipCheckoutTarget | null {
  const storefronts = resources.filter(
    (r): r is NonNullable<ResourceLike> & { _id: string } =>
      !!r?._id && r.type === "STOREFRONT" && !!r.booker?._id,
  );
  if (tierTagId) {
    const bookerId = tierCheckouts?.find(
      c => c?.tierTagId === tierTagId && !!c.bookerId,
    )?.bookerId;
    if (bookerId) {
      const matches = storefronts.filter(r => r.booker?._id === bookerId);
      if (matches.length === 1) return { resourceId: matches[0]._id };
      if (matches.length === 0 && storefronts.length > 0) {
        return { resourceId: storefronts[0]._id, bookerId };
      }
      return null;
    }
  }
  return storefronts.length === 1 ? { resourceId: storefronts[0]._id } : null;
}

export function resolveMembershipCheckoutResourceId(
  args: Parameters<typeof resolveMembershipCheckoutTarget>[0],
): string | null {
  return resolveMembershipCheckoutTarget(args)?.resourceId ?? null;
}

type ProductTierLike = {
  _id?: string | null;
  tagConfig?: { onCheckoutCompleted?: (string | null)[] | null } | null;
  personTagConfig?: { onCheckoutCompleted?: (string | null)[] | null } | null;
};

/**
 * Product tier that grants the membership tier tag on checkout. Membership
 * rows store a Tag id, not a product tier id. Null unless exactly one tier
 * matches, so preselection never guesses.
 */
export function findProductTierIdForTag(
  tiers: readonly ProductTierLike[],
  tierTagId: string | null | undefined,
): string | null {
  if (!tierTagId) return null;
  const matches = tiers.filter(tier =>
    [
      ...(tier.tagConfig?.onCheckoutCompleted ?? []),
      ...(tier.personTagConfig?.onCheckoutCompleted ?? []),
    ].includes(tierTagId),
  );
  return matches.length === 1 ? (matches[0]._id ?? null) : null;
}

const STATUS_SEVERITY: Record<string, number> = {
  LAPSED: 3,
  REVOKED: 3,
  PENDING: 2,
  ACTIVE: 1,
};

export type MembershipOrgGroup<T extends MembershipRowLike> = {
  organizationId: string;
  rows: T[];
  dueRows: T[];
  /** Worst status across rows: LAPSED/REVOKED, then PENDING, then ACTIVE. */
  status: string;
  /** Earliest upcoming renewal date among rows that show one. */
  earliestRenewalDate: string | null;
};

/**
 * One group per organization, keeping first-seen order. Businesses and the
 * person's own membership in an org share one card in the Memberships hub.
 */
export function groupMembershipsByOrganization<
  T extends MembershipRowLike & {
    status: string;
    renewalDate?: string | null;
    renewalDateDisplayMode?: string | null;
  },
>(rows: readonly T[]): MembershipOrgGroup<T>[] {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const list = groups.get(row.organizationId) ?? [];
    list.push(row);
    groups.set(row.organizationId, list);
  }
  return [...groups.entries()].map(([organizationId, orgRows]) => {
    const status = orgRows.reduce(
      (worst, r) =>
        (STATUS_SEVERITY[r.status] ?? 0) > (STATUS_SEVERITY[worst] ?? 0)
          ? r.status
          : worst,
      orgRows[0].status,
    );
    const upcoming = orgRows
      .filter(r => r.renewalDate && r.renewalDateDisplayMode !== "EXPIRED_PERIOD")
      .map(r => r.renewalDate as string)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    return {
      organizationId,
      rows: orgRows,
      dueRows: orgRows.filter(r => r.needsRenewal),
      status,
      earliestRenewalDate: upcoming[0] ?? null,
    };
  });
}

/** Organizations with at least one membership due for renewal, in first-seen order. */
export function organizationsDueForRenewal(
  rows: readonly Pick<MembershipRowLike, "organizationId" | "needsRenewal">[],
): string[] {
  return [...new Set(rows.filter(r => r.needsRenewal).map(r => r.organizationId))];
}
